import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { usePhoneNumbersQuery } from '../../features/phone-numbers';
import type { PhoneNumber } from '../../entities/phone-number';
import { isPhoneNumberActive } from '../../entities/phone-number';
import { usePrefetchEsimSvgs } from '../hooks/use-prefetch-esim-svgs';
import { useTonWallet } from '@tonconnect/ui-react';
import { useWalletsQuery } from '../../features/wallets';
import { walletMatches } from '../../features/wallets/lib/address-normalizers';
import { STORAGE_KEYS } from '../config/storage-keys';

interface ActiveEsimContextValue {
  activeEsim: PhoneNumber | null;
  activeEsimId: string | null;
  setActiveEsim: (id: string) => void;
  clearActiveEsim: () => void;
  ensureActiveEsim: () => void;
  isLoading: boolean;
}

const ActiveEsimContext = createContext<ActiveEsimContextValue | null>(null);

/**
 * Get last used eSIM for specific wallet from localStorage
 */
function getLastUsedEsimForWallet(walletAddress: string): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(STORAGE_KEYS.LAST_USED_ESIM_BY_WALLET);
    if (!stored) return null;

    const map = JSON.parse(stored) as Record<string, string>;
    return map[walletAddress] || null;
  } catch {
    return null;
  }
}

/**
 * Save last used eSIM for specific wallet to localStorage
 */
function saveLastUsedEsimForWallet(walletAddress: string, esimId: string): void {
  if (typeof window === 'undefined') return;

  try {
    const stored = localStorage.getItem(STORAGE_KEYS.LAST_USED_ESIM_BY_WALLET);
    const map = stored ? JSON.parse(stored) : {};
    map[walletAddress] = esimId;
    localStorage.setItem(STORAGE_KEYS.LAST_USED_ESIM_BY_WALLET, JSON.stringify(map));
  } catch (error) {
    console.warn('[ActiveEsimContext] Failed to save last used eSIM:', error);
  }
}

/**
 * Check if eSIM belongs to current wallet
 * IMPORTANT: This prevents selecting eSIMs from other wallets!
 */
function isEsimOwnedByCurrentWallet(
  esim: PhoneNumber,
  currentPhoneNumbers: PhoneNumber[],
  currentWalletId?: string,
): boolean {
  // First check: eSIM must be in the current phone numbers list (already filtered by walletId)
  const inList = currentPhoneNumbers.some((phone) => phone.id === esim.id);
  if (!inList) return false;

  if (currentWalletId !== undefined) {
    return esim.walletId === currentWalletId;
  }

  return true;
}

/**
 * Smart selection of active eSIM based on priority:
 * 1. Last used eSIM for this wallet (if still valid AND owned by wallet)
 * 2. eSIM with highest balance
 * 3. Most recently activated eSIM
 * 4. First eSIM in list
 *
 * SECURITY: Only selects eSIMs from current wallet's phone numbers
 */
function selectBestEsim(phoneNumbers: PhoneNumber[], walletAddress?: string, walletId?: string): PhoneNumber | null {
  const activePhones = phoneNumbers.filter(isPhoneNumberActive);

  // Phone numbers are already filtered by walletId from the query, so we can use them directly
  if (activePhones.length === 0) return null;
  if (activePhones.length === 1) return activePhones[0];

  if (walletAddress) {
    const lastUsedId = getLastUsedEsimForWallet(walletAddress);
    if (lastUsedId) {
      const lastUsed = activePhones.find(phone => phone.id === lastUsedId);
      if (lastUsed && isEsimOwnedByCurrentWallet(lastUsed, activePhones, walletId)) {
        console.log('[ActiveEsimContext] Using last used eSIM for wallet:', lastUsedId);
        return lastUsed;
      } else if (lastUsed) {
        console.warn('[ActiveEsimContext] Last used eSIM found but not owned by current wallet, skipping');
      }
    }
  }

  const withBalance = activePhones
    .map((phone) => ({
      phone,
      balance: Number(phone.balance ?? 0),
    }))
    .filter(({ balance }) => balance > 0)
    .sort((a, b) => b.balance - a.balance);

  if (withBalance.length > 0) {
    console.log('[ActiveEsimContext] Selecting eSIM with highest balance:', withBalance[0].phone.id);
    return withBalance[0].phone;
  }

  const sorted = [...activePhones].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  console.log('[ActiveEsimContext] Selecting most recent eSIM:', sorted[0].id);
  return sorted[0];
}
export function ActiveEsimProvider({ children }: { children: React.ReactNode }) {
  const tonWallet = useTonWallet();
  const walletAddress = tonWallet?.account?.address;
  const { data: wallets, isLoading: isLoadingWallets } = useWalletsQuery();

  const walletId = useMemo(() => {
    if (!walletAddress || !wallets) return undefined;
    const matching = wallets.find((wallet) => walletMatches(wallet, walletAddress));
    return matching?.id;
  }, [walletAddress, wallets]);

  const shouldLoadPhoneNumbers = !!walletId && !isLoadingWallets;
  const { data: phoneNumbers, isLoading } = usePhoneNumbersQuery(
    {
      walletId,
      status: 'ACTIVE',
      requireWallet: true,
      requireUser: false, // Wallet-only access
    },
    { enabled: shouldLoadPhoneNumbers },
  );

  const isSelectingRef = useRef(false);
  const [selectionNonce, bumpSelectionNonce] = useState(0);

  const [activeEsimId, setActiveEsimIdState] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEYS.ACTIVE_ESIM_ID);
    }
    return null;
  });

  const phoneNumberMsisdns = useMemo(
    () => phoneNumbers?.map((phone) => phone.msisdn) ?? [],
    [phoneNumbers]
  );
  usePrefetchEsimSvgs(phoneNumberMsisdns, ['body']);

  const setActiveEsim = useCallback((id: string) => {
    isSelectingRef.current = true;
    setActiveEsimIdState(id);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_ESIM_ID, id);
    }

    if (walletAddress) {
      saveLastUsedEsimForWallet(walletAddress, id);
    }
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 100);
  }, [walletAddress]);

  const clearActiveEsim = useCallback(() => {
    isSelectingRef.current = true;
    setActiveEsimIdState(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_ESIM_ID);
    }
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 100);
  }, []);

  const activeEsim = React.useMemo(() => {
    if (!phoneNumbers || !activeEsimId) return null;
    return phoneNumbers.find(phone => phone.id === activeEsimId) || null;
  }, [phoneNumbers, activeEsimId]);

  const prevWalletAddressRef = useRef<string | undefined>(walletAddress);

  useEffect(() => {
    if (isLoading || isLoadingWallets) return;

    // wait for walletId resolution before acting on phone numbers
    if (walletAddress && !walletId) return;

    const walletChanged = prevWalletAddressRef.current !== walletAddress;

    if (isSelectingRef.current && !walletChanged) return;

    if (!walletAddress) {
      if (activeEsimId) {
        console.log('[ActiveEsimContext] Wallet disconnected, clearing active eSIM');
        setActiveEsimIdState(null);
        if (typeof window !== 'undefined') {
          localStorage.removeItem(STORAGE_KEYS.ACTIVE_ESIM_ID);
        }
      }
      prevWalletAddressRef.current = undefined;
      return;
    }

    // If no phone numbers available, clear selection if needed
    if (!phoneNumbers || phoneNumbers.length === 0) {
      if (activeEsimId) {
        console.log('[ActiveEsimContext] No phone numbers available, clearing active eSIM');
        setActiveEsimIdState(null);
        if (typeof window !== 'undefined') {
          localStorage.removeItem(STORAGE_KEYS.ACTIVE_ESIM_ID);
        }
      }
      return;
    }

    if (walletChanged) {
      console.log('[ActiveEsimContext] Wallet changed, clearing current selection and re-selecting');
      prevWalletAddressRef.current = walletAddress;

      isSelectingRef.current = true;
      setActiveEsimIdState(null);

      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEYS.ACTIVE_ESIM_ID);
      }

      // Continue to select best eSIM for new wallet
      // Don't return early, let the selection logic run
    }

    // Check if current selection is still valid (only if wallet didn't change)
    if (activeEsimId && !walletChanged) {
      const current = phoneNumbers.find(phone => phone.id === activeEsimId);
      if (current && isPhoneNumberActive(current) && isEsimOwnedByCurrentWallet(current, phoneNumbers, walletId)) {
        return; // Current selection is still valid
      }

      if (!current || current.status !== 'ACTIVE') {
        console.log('[ActiveEsimContext] Current eSIM is no longer valid, selecting new one');
      } else {
        console.warn('[ActiveEsimContext] Current eSIM not owned by wallet, clearing');
      }
    }

    // Select best eSIM (for new wallet or if current is invalid)
    const bestEsim = selectBestEsim(phoneNumbers, walletAddress, walletId);
    if (bestEsim && bestEsim.id !== activeEsimId) {
      isSelectingRef.current = true;
      setActiveEsimIdState(bestEsim.id);

      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEYS.ACTIVE_ESIM_ID, bestEsim.id);
      }

      saveLastUsedEsimForWallet(walletAddress, bestEsim.id);
      requestAnimationFrame(() => {
        isSelectingRef.current = false;
      });
    } else if (!bestEsim && activeEsimId) {
      // No valid eSIM found, clear selection
      isSelectingRef.current = true;
      setActiveEsimIdState(null);

      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEYS.ACTIVE_ESIM_ID);
      }

      requestAnimationFrame(() => {
        isSelectingRef.current = false;
      });
    } else if (walletChanged && isSelectingRef.current) {
      // Reset selecting flag after wallet change
      requestAnimationFrame(() => {
        isSelectingRef.current = false;
      });
    }
  }, [phoneNumbers, isLoading, walletAddress, selectionNonce, walletId, isLoadingWallets]); // Removed activeEsimId from dependencies!

  const ensureActiveEsim = useCallback(() => {
    bumpSelectionNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    // Ensure selection logic runs on initial render
    ensureActiveEsim();
  }, [ensureActiveEsim]);

  const value: ActiveEsimContextValue = {
    activeEsim,
    activeEsimId,
    setActiveEsim,
    clearActiveEsim,
    ensureActiveEsim,
    isLoading,
  };

  return (
    <ActiveEsimContext.Provider value={value}>
      {children}
    </ActiveEsimContext.Provider>
  );
}

export function useActiveEsim(): ActiveEsimContextValue {
  const context = useContext(ActiveEsimContext);
  if (!context) {
    throw new Error('useActiveEsim must be used within ActiveEsimProvider');
  }
  return context;
}

